const ENCRYPTION_ALGORITHM = "AES-GCM";
const IV_LENGTH = 12;

const getEncryptionSecret = (): string =>
  process.env.REACT_APP_ENCRYPTION_KEY || "default_encryption";

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const fromBase64Url = (value: string): Uint8Array => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const getKey = async (): Promise<CryptoKey> => {
  const secretBytes = new TextEncoder().encode(getEncryptionSecret());
  const keyHash = await crypto.subtle.digest("SHA-256", secretBytes);

  return crypto.subtle.importKey(
    "raw",
    keyHash,
    { name: ENCRYPTION_ALGORITHM },
    false,
    ["encrypt", "decrypt"],
  );
};

export const encryptPayload = async (payload: unknown): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await getKey();
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const encrypted = new Uint8Array(
    await crypto.subtle.encrypt({ name: ENCRYPTION_ALGORITHM, iv }, key, plaintext),
  );
  const output = new Uint8Array(iv.length + encrypted.length);

  output.set(iv);
  output.set(encrypted, iv.length);

  return toBase64Url(output);
};

export const decryptPayload = async <T = unknown>(payload: string): Promise<T> => {
  const encrypted = fromBase64Url(payload);
  const iv = encrypted.slice(0, IV_LENGTH);
  const ciphertext = encrypted.slice(IV_LENGTH);
  const key = await getKey();
  const decrypted = await crypto.subtle.decrypt(
    { name: ENCRYPTION_ALGORITHM, iv },
    key,
    ciphertext,
  );

  return JSON.parse(new TextDecoder().decode(decrypted)) as T;
};

export const isEncryptedEnvelope = (value: unknown): value is { payload: string } =>
  !!value &&
  typeof value === "object" &&
  typeof (value as { payload?: unknown }).payload === "string";
